import { useState, useEffect } from 'react';
import { zpanelApi } from '../../utils/zpanelApi';

const durationLabels = { monthly: 'Aylık', yearly: 'Yıllık', lifetime: 'Süresiz' };

const SubscriptionPlansPage = () => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: '', code: '', description: '', features: '', price: 0, currency: 'TRY', duration_type: 'monthly', duration_days: 30, price_yearly: null, sort_order: 0 });
    const [saving, setSaving] = useState(false);

    const fetchPlans = async () => {
        setLoading(true);
        try {
            const res = await zpanelApi.listSubscriptionPlans();
            setPlans(res.data || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchPlans(); }, []);

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const features = form.features ? form.features.split('\n').map(s => s.trim()).filter(Boolean) : [];
            const data = { ...form, features, price: parseFloat(form.price) || 0, price_yearly: form.price_yearly ? parseFloat(form.price_yearly) : null };
            if (editing) {
                await zpanelApi.updateSubscriptionPlan(editing.id, data);
            } else {
                await zpanelApi.createSubscriptionPlan(data);
            }
            setEditing(null);
            setForm({ name: '', code: '', description: '', features: '', price: 0, currency: 'TRY', duration_type: 'monthly', duration_days: 30, price_yearly: null, sort_order: 0 });
            fetchPlans();
        } catch (err) {
            alert(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (plan) => {
        setEditing(plan);
        setForm({
            name: plan.name,
            code: plan.code,
            description: plan.description || '',
            features: Array.isArray(plan.features) ? plan.features.join('\n') : (plan.features || ''),
            price: plan.price || 0,
            currency: plan.currency || 'TRY',
            duration_type: plan.duration_type || 'monthly',
            duration_days: plan.duration_days || 30,
            price_yearly: plan.price_yearly || '',
            sort_order: plan.sort_order || 0
        });
    };

    const handleDelete = async (planId) => {
        if (!confirm('Bu planı silmek istediğinize emin misiniz?')) return;
        try {
            await zpanelApi.deleteSubscriptionPlan(planId);
            fetchPlans();
        } catch (err) {
            alert(err.message);
        }
    };

    return (
        <div className="p-6">
            <h1 className="text-2xl font-bold text-white mb-6">Abonelik Planları</h1>

            <form onSubmit={handleSave} className="mb-8 bg-slate-800 rounded-xl border border-slate-700 p-6">
                <h2 className="text-lg font-semibold text-white mb-4">{editing ? 'Plan Düzenle' : 'Yeni Plan'}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm text-slate-400 mb-1">Plan Adı *</label>
                        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                    </div>
                    <div>
                        <label className="block text-sm text-slate-400 mb-1">Kod (free, basic, premium) *</label>
                        <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" placeholder="free" disabled={!!editing} />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm text-slate-400 mb-1">Açıklama</label>
                        <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                    </div>
                    <div>
                        <label className="block text-sm text-slate-400 mb-1">Özellikler (her satıra bir özellik)</label>
                        <textarea value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} rows={3} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" placeholder="5 kategori&#10;20 ürün" />
                    </div>
                    <div>
                        <label className="block text-sm text-slate-400 mb-1">Fiyat</label>
                        <div className="flex gap-2">
                            <input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                            <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white w-24">
                                <option value="TRY">TRY</option>
                                <option value="USD">USD</option>
                                <option value="EUR">EUR</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm text-slate-400 mb-1">Süre Tipi</label>
                        <select value={form.duration_type} onChange={(e) => setForm({ ...form, duration_type: e.target.value })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white">
                            {Object.entries(durationLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm text-slate-400 mb-1">Süre (gün)</label>
                        <input type="number" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value || null })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" placeholder="30, 365" />
                    </div>
                    <div>
                        <label className="block text-sm text-slate-400 mb-1">Yıllık Fiyat (indirimli)</label>
                        <input type="number" step="0.01" value={form.price_yearly} onChange={(e) => setForm({ ...form, price_yearly: e.target.value || null })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" placeholder="Opsiyonel" />
                    </div>
                </div>
                <div className="flex gap-2 mt-4">
                    <button type="submit" disabled={saving} className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg disabled:opacity-50">{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
                    {editing && <button type="button" onClick={() => { setEditing(null); setForm({ name: '', code: '', description: '', features: '', price: 0, currency: 'TRY', duration_type: 'monthly', duration_days: 30, price_yearly: null, sort_order: 0 }); }} className="px-4 py-2 bg-slate-700 text-white rounded-lg">İptal</button>}
                </div>
            </form>

            <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                <table className="w-full">
                    <thead className="bg-slate-700/50">
                        <tr>
                            <th className="text-left px-4 py-3 text-slate-300">Ad</th>
                            <th className="text-left px-4 py-3 text-slate-300">Kod</th>
                            <th className="text-left px-4 py-3 text-slate-300">Fiyat</th>
                            <th className="text-left px-4 py-3 text-slate-300">Süre</th>
                            <th className="px-4 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {plans.map((p) => (
                            <tr key={p.id} className="border-t border-slate-700">
                                <td className="px-4 py-3 text-white">{p.name}</td>
                                <td className="px-4 py-3 text-amber-400">{p.code}</td>
                                <td className="px-4 py-3 text-slate-300">{p.price} {p.currency}</td>
                                <td className="px-4 py-3 text-slate-400">{durationLabels[p.duration_type] || p.duration_type} {p.duration_days ? `(${p.duration_days} gün)` : ''}</td>
                                <td className="px-4 py-3">
                                    <button onClick={() => handleEdit(p)} className="text-amber-400 hover:text-amber-300 text-sm mr-3">Düzenle</button>
                                    <button onClick={() => handleDelete(p.id)} className="text-red-400 hover:text-red-300 text-sm">Sil</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {loading && <div className="p-8 text-center text-slate-500">Yükleniyor...</div>}
                {!loading && plans.length === 0 && <div className="p-8 text-center text-slate-500">Plan bulunamadı</div>}
            </div>
        </div>
    );
};

export default SubscriptionPlansPage;
